<template>
  <div class="segment-mapping-carousel">
    <!-- Header with toggle -->
    <div class="carousel-header">
      <div class="carousel-title-area">
        <h3 class="carousel-title">Media Uploads</h3>
        <span class="upload-count" v-if="userUploads.length > 0">({{ userUploads.length }})</span>
      </div>
      <div class="carousel-controls">
        <button
          class="toggle-btn"
          @click="isExpanded = !isExpanded"
          title="Toggle media uploads view"
        >
          {{ isExpanded ? '▼' : '▶' }}
        </button>
      </div>
    </div>

    <!-- Collapsed View -->
    <div v-if="!isExpanded && userUploads.length > 0" class="collapsed-view">
      <div class="collapsed-preview">
        <div class="collapsed-thumbnails">
          <div
            v-for="upload in userUploads.slice(0, 4)"
            :key="upload.id"
            class="collapsed-thumbnail"
            @click="expandAndPreview(upload)"
            :title="`View ${upload.file.name}`"
          >
            <!-- Image thumbnails -->
            <img v-if="isImageFile(upload.file.type)"
                 :src="upload.preview"
                 :alt="upload.file.name">
            <!-- Video thumbnails -->
            <div v-else-if="isVideoFile(upload.file.type)" class="video-thumb-mini">
              <span>▶</span>
            </div>
            <!-- Other file type icons -->
            <div v-else class="file-icon-mini" :class="getFileIconClass(upload.file.type)">
              {{ getFileExtension(upload.file.name).substring(0, 3).toUpperCase() }}
            </div>
          </div>
          <div v-if="userUploads.length > 4" class="more-indicator">
            +{{ userUploads.length - 4 }}
          </div>
        </div>
        <div class="collapsed-info">
          <span class="collapsed-text">{{ userUploads.length }} media uploaded</span>
          <button class="quick-upload-btn" @click="triggerUpload" v-if="isPro" title="Quick upload">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Expanded Content -->
    <div v-if="isExpanded" class="expanded-content">
      <!-- Upload Section -->
      <div class="upload-section">
  <label :for="`file-upload-${messageId}`" class="upload-button" :class="{ 'disabled': isUploading }" title="Upload images or videos">
    <div class="upload-icon">
      <svg v-if="!isUploading" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
      </svg>
      <!-- Spinner animation when uploading -->
      <div v-else class="spinner"></div>
    </div>
    <span v-if="!isUploading">Upload Media</span>
    <span v-else>Uploading {{ uploadProgress }}</span>
  </label>
  <input
    :id="`file-upload-${messageId}`"
    ref="fileInput"
    type="file"
    multiple
    accept="image/*,video/*,video/mp4,.mp4,.mov,video/quicktime,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
    @change="handleFileUpload"
    class="file-input"
    :disabled="isUploading"
  />
</div>

      <!-- Filter Controls - NEW SECTION -->
      <div v-if="isExpanded && userUploads.length > 0" class="filter-controls">
        <div class="filter-title">Filter by:</div>
        <div class="filter-buttons">
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'all' }"
            @click="setFilter('all')"
          >
            All ({{ mediaCounts.all }})
          </button>
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'image' }"
            @click="setFilter('image')"
            v-if="mediaCounts.image > 0"
          >
            Images ({{ mediaCounts.image }})
          </button>
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'video' }"
            @click="setFilter('video')"
            v-if="mediaCounts.video > 0"
          >
            Videos ({{ mediaCounts.video }})
          </button>
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'document' }"
            @click="setFilter('document')"
            v-if="mediaCounts.document > 0"
          >
            Documents ({{ mediaCounts.document }})
          </button>
        </div>
        <div class="sort-controls">
          <label for="sort-select" class="sort-label">Sort:</label>
          <select id="sort-select" v-model="sortBy" class="sort-select">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name">Name</option>
          </select>
        </div>
        <div class="search-box">
          <input
            type="text"
            placeholder="Search uploads..."
            class="search-input"
            v-model="searchQuery"
            @input="filterBySearch"
          >
          <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
      </div>

      <!-- Uploads Display Carousel - UPDATED to use displayedUploads -->
      <div v-if="userUploads.length > 0" class="carousel-container">
        <div class="scroll-arrow left" @click.stop="scrollCarousel('left')" v-if="showScrollButtons && hasScrollLeft">
          <span>◀</span>
        </div>

        <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
          <div
            v-for="(upload, index) in displayedUploads"
            :key="upload.id"
            class="carousel-item"
            :class="{ 'active': index === activeUploadIndex }"
            @click="openPreview(upload)"
            :title="`View segment mapping: ${upload.file.name}`"
          >
            <div class="upload-card">
              <div class="upload-image">
                <!-- Handle image files -->
                <img v-if="isImageFile(upload.file.type)"
                    :src="upload.preview"
                    :alt="upload.file.name"
                    @error="handleMediaError">
                <!-- Handle video files -->
                <div v-else-if="isVideoFile(upload.file.type)" class="video-thumbnail">
                  <div class="video-icon">▶</div>
                  <img v-if="upload.preview"
                       :src="upload.preview"
                       :alt="upload.file.name"
                       @error="handleMediaError">
                  <div v-else class="video-placeholder">
                    MP4
                  </div>
                </div>
                <!-- Handle other file types -->
                <div v-else class="file-icon" :class="getFileIconClass(upload.file.type)">
                  {{ getFileExtension(upload.file.name).toUpperCase() }}
                </div>
              </div>
              <div class="upload-info">
                <div class="upload-title">{{ truncateFileName(upload.file.name) }}</div>
                <div class="upload-meta">
                  {{ formatFileSize(upload.file.size) }} · {{ formatFileType(upload.file.type) }}
                </div>
              </div>
              <!-- Media type badges - NEW -->
              <div
                v-if="isImageFile(upload.file.type)"
                class="media-type-badge image-badge"
              >
                Image
              </div>
              <div
                v-if="isVideoFile(upload.file.type)"
                class="media-type-badge video-badge"
              >
                Video
              </div>
              <div
                v-if="!isImageFile(upload.file.type) && !isVideoFile(upload.file.type)"
                class="media-type-badge doc-badge"
              >
                Doc
              </div>
              <button class="remove-upload" @click.stop="removeUpload(index)" title="Remove this segment mapping">×</button>
            </div>
          </div>
        </div>

        <div class="scroll-arrow right" @click.stop="scrollCarousel('right')" v-if="showScrollButtons && hasScrollRight">
          <span>▶</span>
        </div>
      </div>

      <!-- No Results Message - NEW -->
      <div v-if="isExpanded && userUploads.length > 0 && displayedUploads.length === 0" class="no-results">
        <p>No {{ activeFilter === 'all' ? 'media' : activeFilter + 's' }} found matching your criteria.</p>
        <button class="reset-filter-btn" @click="resetFilters">Reset filters</button>
      </div>
    </div>

    <!-- Empty state for expanded view -->
    <div v-if="isExpanded && userUploads.length === 0" class="empty-state">
      <p>No media uploaded yet. Click the upload button to add media.</p>
    </div>

    <!-- Preview Modal -->
    <div v-if="previewOpen" class="preview-modal" @click="closePreview">
      <div class="preview-content" @click.stop>
        <div class="preview-header">
          <h3>{{ activeUpload ? activeUpload.file.name : '' }}</h3>
          <button class="close-preview" @click="closePreview" title="Close preview">×</button>
        </div>
        <div class="preview-body">
          <!-- Image preview -->
          <img v-if="activeUpload && isImageFile(activeUpload.file.type)"
               :src="activeUpload.preview"
               :alt="activeUpload.file.name">

          <!-- Video preview -->
          <video v-else-if="activeUpload && isVideoFile(activeUpload.file.type)"
                 controls
                 autoplay
                 muted
                 preload="metadata"
                 loop
                 class="preview-video"
                 :src="activeUpload.dataUrl"
                 :alt="activeUpload.file.name"
                 @error="handleMediaError"></video>

          <!-- Other file types preview -->
          <div v-else class="preview-placeholder">
            <div class="file-icon large" :class="getFileIconClass(activeUpload ? activeUpload.file.type : '')">
              {{ getFileExtension(activeUpload ? activeUpload.file.name : '').toUpperCase() }}
            </div>
            <p>Preview not available</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress Overlay -->
    <div v-if="isUploading" class="upload-overlay">
      <div class="upload-progress-container">
        <div class="upload-progress-spinner"></div>
        <div class="upload-progress-text">
          <div class="upload-file-name">{{ currentUploadingFile }}</div>
          <div class="upload-progress-status">{{ uploadProgressMessage }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/api';
import { mapActions } from 'vuex';

export default {
  name: 'UserUploadsCarousel',
  props: {
    messageId: {
      required: true
    },
    isPro: {
      type: Boolean,
      default: false
    },
    showUploadSection: {
      type: Boolean,
      default: false
    },
    isExpandedView: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isExpanded: false, // Start collapsed
      userUploads: [],
      showScrollButtons: true,
      hasScrollLeft: false,
      hasScrollRight: true,
      activeUploadIndex: -1,
      previewOpen: false,
      activeUpload: null,
      uploadCounter: 0,
      isUploading: false,
      uploadProgress: '0%',
      currentUploadingFile: '',
      totalFiles: 0,
      completedFiles: 0,
      uploadProgressMessage: '',

      // NEW properties for filtering and sorting
      activeFilter: 'all', // Options: 'all', 'image', 'video', 'document'
      searchQuery: '',
      debouncedSearchTimeout: null,
      sortBy: 'newest', // Options: 'newest', 'oldest', 'name'
    }
  },
  computed: {
    documentIdKey() {
      return `uploads-${this.messageId}`;
    },
    uploadKeys() {
      return this.userUploads.map(upload => `${this.messageId}_${upload.id}`);
    },

    // NEW computed properties for filtering
    mediaCounts() {
      const counts = {
        all: this.userUploads.length,
        image: 0,
        video: 0,
        document: 0
      };

      this.userUploads.forEach(upload => {
        if (this.isImageFile(upload.file.type)) {
          counts.image++;
        } else if (this.isVideoFile(upload.file.type)) {
          counts.video++;
        } else {
          counts.document++;
        }
      });

      return counts;
    },

    // Filtered and sorted uploads
    displayedUploads() {
      // Start with all uploads
      let filtered = [...this.userUploads];

      // Apply type filter
      if (this.activeFilter !== 'all') {
        filtered = filtered.filter(upload => {
          if (this.activeFilter === 'image') {
            return this.isImageFile(upload.file.type);
          } else if (this.activeFilter === 'video') {
            return this.isVideoFile(upload.file.type);
          } else if (this.activeFilter === 'document') {
            return !this.isImageFile(upload.file.type) && !this.isVideoFile(upload.file.type);
          }
          return true;
        });
      }

      // Apply search query filter if it exists
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(upload => {
          // Search in filename
          return upload.file.name.toLowerCase().includes(query);
        });
      }

      // Apply sorting
      if (this.sortBy === 'newest') {
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      } else if (this.sortBy === 'oldest') {
        filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      } else if (this.sortBy === 'name') {
        filtered.sort((a, b) => a.file.name.localeCompare(b.file.name));
      }

      return filtered;
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),

    // NEW methods for filtering
    setFilter(filter) {
      this.activeFilter = filter;
    },

    filterBySearch() {
      // Clear any existing timeout to debounce rapid typing
      if (this.debouncedSearchTimeout) {
        clearTimeout(this.debouncedSearchTimeout);
      }

      // Set a timeout to avoid filtering on each keystroke
      this.debouncedSearchTimeout = setTimeout(() => {
        // The actual filtering is handled by the displayedUploads computed property
        // We just need to trigger its recalculation
        this.$forceUpdate();
      }, 300);
    },

    resetFilters() {
      this.activeFilter = 'all';
      this.searchQuery = '';
      this.sortBy = 'newest';
    },

    expandAndPreview(upload) {
      this.isExpanded = true;
      this.$nextTick(() => {
        this.openPreview(upload);
      });
    },

    triggerUpload() {
      this.isExpanded = true;
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.click();
        }
      });
    },

    async handleFileUpload(event) {

      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Initialize upload tracking variables
      this.isUploading = true;
      this.totalFiles = files.length;
      this.completedFiles = 0;
      this.uploadProgress = '0%';
      this.uploadProgressMessage = `Preparing to upload ${this.totalFiles} file(s)...`;

      // Process each file sequentially to avoid overwhelming the server
      for (const [index, file] of Array.from(files).entries()) {
        // Set current file info
        this.currentUploadingFile = file.name;
        this.uploadProgressMessage = `Uploading file ${index + 1} of ${this.totalFiles}`;

        // Check if file size is less than 50MB
        if (file.size > 50 * 1024 * 1024) {
          this.$emit('upload-error', 'File size exceeds 50MB limit');
          this.showTempMessageFn({
            message: `File size exceeds 50MB limit for ${file.name}`,
            type: 'error',
            timeout: 5000
          });
          continue;
        }

        try {
          // Create form data for this file
          const formData = new FormData();
          formData.append('file', file);

          // Upload to backend API with progress tracking
          const response = await API.post(`/user_customization/messages/${this.messageId}/documents`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              // Calculate and update the progress percentage for this file
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.uploadProgress = `${percentCompleted}%`;
            }
          });

          // Parse the response data
          const documentData = response.data;

          // Create a local representation of the document
          const upload = {
            id: documentData.id,
            file: {
              name: documentData.filename,
              type: documentData.type,
              size: documentData.size
            },
            preview: documentData.thumbnailUrl || documentData.url,
            timestamp: new Date().toISOString(),
            uniqueKey: documentData.uniqueKey,
            dataUrl: documentData.url
          };

          // Add to local collection
          this.userUploads.push(upload);

          // Increment completed files counter
          this.completedFiles++;
          this.uploadProgressMessage = `Completed ${this.completedFiles} of ${this.totalFiles} files`;

          // Emit event with upload data
          this.$emit('upload-added', {
            messageId: this.messageId,
            upload: upload,
            uniqueKey: documentData.uniqueKey,
            dataUrl: documentData.url
          });

        } catch (error) {
          console.error('Error uploading document:', error);
          this.showTempMessageFn({
            message: `Failed to upload ${file.name}. Are you the owner of this show?`,
            type: 'error',
            timeout: 5000
          });
          this.$emit('upload-error', `Failed to upload ${file.name}: ${error.message}`);
        }
      }

      // Reset upload status after all files are processed
      this.isUploading = false;
      this.currentUploadingFile = '';
      this.uploadProgress = '0%';
      this.uploadProgressMessage = '';

      // Show a success message after all uploads complete
      this.showTempMessageFn({
        message: `Successfully uploaded ${this.completedFiles} file(s).`,
        type: 'success',
        timeout: 3000
      });

      // Clear input value to allow uploading the same file again
      event.target.value = '';
    },

    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';
      return 'generic-icon';
    },

    formatFileSize(size) {
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
      return Math.round(size / (1024 * 1024) * 10) / 10 + ' MB';
    },

    formatFileType(type) {
      if (!type) return 'UNKNOWN';
      const fileType = type.split('/').pop().toUpperCase();
      return fileType === 'MP4' ? 'VIDEO' : fileType;
    },

    truncateFileName(name) {
      return name.length > 20 ? name.substring(0, 17) + '...' : name;
    },

    // Remove a document
    async removeUpload(index) {
      // Find the upload in the filtered view
      const uploadToRemove = this.displayedUploads[index];
      if (!uploadToRemove) return;

      // Find the original index in the userUploads array
      const originalIndex = this.userUploads.findIndex(upload => upload.id === uploadToRemove.id);
      if (originalIndex === -1) return;

      try {
        // Call backend API to delete the document
        await API.delete(`/user_customization/messages/${this.messageId}/documents/${uploadToRemove.id}`);

        // Remove from local collection
        this.userUploads.splice(originalIndex, 1);

        // Emit event
        this.$emit('upload-removed', {
          messageId: this.messageId,
          uploadId: uploadToRemove.id,
          uniqueKey: uploadToRemove.uniqueKey
        });
      } catch (error) {
        console.error('Error removing document:', error);
        // You might want to show an error message to the user
      }
    },

    // Load uploads from backend instead of localStorage
    async loadUploadsFromStorage() {
      try {
        // Fetch documents from backend API
        const token = localStorage.getItem('token');
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token ? `/user_customization/messages/${this.messageId}/documents` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/documents`;
        const response = await API.get(url);
        const documentsData = response.data;

        // Reset local collection
        this.userUploads = [];

        // Process documents from the response
        if (documentsData && documentsData[this.messageId]) {
          Object.values(documentsData[this.messageId]).forEach(doc => {
            this.userUploads.push({
              id: doc.id,
              file: {
                name: doc.name,
                type: doc.type,
                size: doc.size
              },
              preview: doc.preview || doc.dataUrl,
              timestamp: doc.timestamp,
              uniqueKey: doc.uniqueKey,
              dataUrl: doc.dataUrl
            });
          });
        }

        this.$emit('uploads-loaded', {
          messageId: this.messageId,
          uploadsCount: this.userUploads.length,
          uploads: this.userUploads
        });

      } catch (error) {
        console.error('Error loading documents:', error);
      }
    },

    scrollCarousel(direction) {
      if (!this.$refs.carouselTrack) return;

      const scrollAmount = 300;
      const currentScroll = this.$refs.carouselTrack.scrollLeft;

      if (direction === 'left') {
        this.$refs.carouselTrack.scrollLeft = currentScroll - scrollAmount;
      } else {
        this.$refs.carouselTrack.scrollLeft = currentScroll + scrollAmount;
      }
    },

    handleScroll() {
      if (!this.$refs.carouselTrack) return;

      const track = this.$refs.carouselTrack;

      // Check if we can scroll left or right
      this.hasScrollLeft = track.scrollLeft > 0;
      this.hasScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth);
    },

    handleMediaError(e) {
      e.target.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      // Add a message about the failed load
      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    openPreview(upload) {
      this.activeUpload = upload;
      this.previewOpen = true;

      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
    },

    closePreview() {
      this.previewOpen = false;
      this.activeUpload = null;

      // Restore body scrolling
      document.body.style.overflow = '';
    },
  },
  mounted() {
    this.$nextTick(() => {
      // check if messageId is available and is an integer
      if (this.messageId && !isNaN(this.messageId)) {
        this.loadUploadsFromStorage();
      }

      if (this.$refs.carouselTrack) {
        this.handleScroll();
      }
    });
  }
}
</script>

<style scoped>
.segment-mapping-carousel {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  margin: 1rem auto;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
}

.theme--dark .segment-mapping-carousel {
  background-color: #1a202c;
}

/* Header */
.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.carousel-title-area {
  display: flex;
  align-items: center;
}

.carousel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .carousel-title {
  color: #e2e8f0;
}

.upload-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.carousel-controls {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

/* Collapsed View */
.collapsed-view {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 6px;
  background-color: #f7fafc;
  cursor: pointer;
}

.theme--dark .collapsed-view {
  background-color: #2d3748;
}

.collapsed-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.collapsed-thumbnails {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.collapsed-thumbnail {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  position: relative;
}

.theme--dark .collapsed-thumbnail {
  border-color: #4a5568;
  background-color: #1a202c;
}

.collapsed-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-thumb-mini {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e53e3e;
  color: white;
  font-size: 0.75rem;
}

.file-icon-mini {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: bold;
  color: white;
}

.more-indicator {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  color: #4a5568;
  font-size: 0.75rem;
  font-weight: 600;
}

.theme--dark .more-indicator {
  background-color: #4a5568;
  color: #e2e8f0;
}

.collapsed-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.collapsed-text {
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .collapsed-text {
  color: #a0aec0;
}

.quick-upload-btn {
  padding: 0.25rem;
  background: none;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.quick-upload-btn:hover {
  background-color: rgba(229, 62, 62, 0.1);
}

.quick-upload-btn svg {
  display: block;
}

/* Expanded Content */
.expanded-content {
  margin-top: 0.75rem;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 2rem;
  color: #718096;
  font-size: 0.875rem;
}

/* All other existing styles remain the same... */

/* Upload Section */
.upload-section {
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
}

/* Enhanced Upload Button Styling */
.upload-button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  border: 2px dashed #e53e3e;
  border-radius: 8px;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: rgba(229, 62, 62, 0.05);
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.1);
}

.upload-button:hover:not(.disabled) {
  border-color: #e53e3e;
  background-color: rgba(229, 62, 62, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(229, 62, 62, 0.2);
}

.upload-button.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  border-style: solid;
  transform: none;
  box-shadow: none;
}

/* Enhanced Upload Text Styling */
.upload-button span {
  font-weight: 600;
  font-size: 1.05rem;
  letter-spacing: 0.02em;
  background: linear-gradient(90deg, #e53e3e, #ff0066);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  text-shadow: 0 1px 2px rgba(229, 62, 62, 0.2);
  position: relative;
}

/* Add pulsing animation to the text */
.upload-button:not(.disabled) span::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #e53e3e, #ff0066);
  opacity: 0.7;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.5;
    transform: scaleX(0.9);
  }
  50% {
    opacity: 1;
    transform: scaleX(1);
  }
}

/* Enhanced Upload Icon */
.upload-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.upload-button:hover:not(.disabled) .upload-icon {
  transform: translateY(-3px);
}

.upload-icon svg {
  stroke: #e53e3e;
  stroke-width: 2.5px;
  width: 28px;
  height: 28px;
  filter: drop-shadow(0 2px 3px rgba(229, 62, 62, 0.3));
}

/* Spinner Animation */
.spinner {
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 3px solid #e53e3e;
  width: 28px;
  height: 28px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.file-input {
  display: none;
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  display: flex;
  margin: 0 0 1rem 0;
  align-items: center;
  padding: 0;
}

.carousel-track {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0.5rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  gap: 16px;
}

.carousel-track::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.carousel-item {
  flex: 0 0 auto;
  width: 180px;
  height: 170px;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.carousel-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00ff00 0%, #ff00ff 33%, #00ffff 66%, #ff00ff 100%);
  background-size: 200% 100%;
  animation: gradientSlide 1.5s linear infinite;
}

.upload-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(4, 255, 134, 0.07);
  border: 1px solid rgba(4, 255, 134, 0.15);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
}

.upload-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  border-color: rgba(4, 255, 134, 0.3);
  background: rgba(4, 255, 134, 0.12);
}

.upload-image {
  height: 65%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.03);
}

.upload-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.upload-card:hover .upload-image img {
  transform: scale(1.05);
}

/* Video specific styles */
.video-thumbnail {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-icon {
  position: absolute;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(4, 255, 134, 0.7);
  color: white;
  border-radius: 50%;
  font-size: 1.2rem;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  color: white;
  font-size: 1rem;
  font-weight: bold;
}

.preview-video {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 4px;
}

.upload-info {
  padding: 10px;
  height: 35%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.upload-title {
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 4px;
}

.upload-meta {
  font-size: 0.7rem;
  color: gray;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-upload {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  font-size: 16px;
  font-weight: bold;
  color: #ff0066;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.upload-card:hover .remove-upload {
  opacity: 1;
}

/* File Icons */
.file-icon {
  width: 80px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  color: white;
  position: relative;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.file-icon.large {
  width: 120px;
  height: 150px;
  font-size: 2rem;
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; }
.generic-icon:before { background: #9E9E9E; }

/* Scroll Arrows */
.scroll-arrow {
  position: absolute;
  width: 40px;
  height: 40px;
  background: rgba(4, 255, 134, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
}

.scroll-arrow:hover {
  background: rgba(4, 255, 134, 0.3);
}

.scroll-arrow.left {
  left: 10px;
}

.scroll-arrow.right {
  right: 10px;
}

.scroll-arrow span {
  color: white;
  font-size: 0.9rem;
}

/* Preview Modal */
.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.preview-content {
  width: 90%;
  max-width: 900px;
  max-height: 80vh;
  background: #222;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(4, 255, 134, 0.1);
  border-bottom: 1px solid rgba(4, 255, 134, 0.2);
}

.preview-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: white;
  font-weight: 500;
}

.close-preview {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
}

.preview-body {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.preview-body img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
}

.preview-placeholder p {
  color: #aaa;
  font-size: 1.1rem;
}

.media-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

/* Upload Overlay */
.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;
  backdrop-filter: blur(2px);
}

.upload-progress-container {
  background: #222;
  padding: 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(4, 255, 134, 0.2);
  max-width: 90%;
}

.upload-progress-spinner {
  border: 4px solid rgba(4, 255, 134, 0.1);
  border-radius: 50%;
  border-top: 4px solid rgba(4, 255, 134, 0.8);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

.upload-progress-text {
  flex: 1;
  min-width: 0;
}

.upload-file-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: white;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-progress-status {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

/* NEW STYLES FOR FILTERING */
/* Filter Controls Styling */
.filter-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(4, 255, 134, 0.15);
}

.filter-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: #4a5568;
}

.theme--dark .filter-title {
  color: #a0aec0;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-btn {
  padding: 6px 12px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.theme--dark .filter-btn {
  background: #2d3748;
  border-color: #4a5568;
  color: #a0aec0;
}

.filter-btn:hover {
  background: #edf2f7;
  transform: translateY(-1px);
}

.theme--dark .filter-btn:hover {
  background: #4a5568;
}

.filter-btn.active {
  background: rgba(4, 255, 134, 0.1);
  border-color: rgba(4, 255, 134, 0.3);
  color: rgba(4, 255, 134, 0.9);
  font-weight: 500;
}

.theme--dark .filter-btn.active {
  background: rgba(4, 255, 134, 0.2);
  color: rgba(4, 255, 134, 1);
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-label {
  font-size: 0.85rem;
  color: #4a5568;
}

.theme--dark .sort-label {
  color: #a0aec0;
}

.sort-select {
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8rem;
  background: #f7fafc;
  color: #4a5568;
  cursor: pointer;
}

.theme--dark .sort-select {
  background: #2d3748;
  border-color: #4a5568;
  color: #a0aec0;
}

.search-box {
  position: relative;
  margin-left: auto;
}

.search-input {
  padding: 6px 12px 6px 32px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8rem;
  width: 180px;
  transition: all 0.2s ease;
  background: #f7fafc;
}

.theme--dark .search-input {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.search-input:focus {
  outline: none;
  border-color: rgba(4, 255, 134, 0.4);
  box-shadow: 0 0 0 2px rgba(4, 255, 134, 0.1);
  width: 220px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.theme--dark .search-icon {
  color: #718096;
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: #718096;
  font-size: 0.875rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  margin: 1rem 0;
}

.theme--dark .no-results {
  background: rgba(255, 255, 255, 0.05);
}

.reset-filter-btn {
  background: none;
  border: none;
  color: rgba(4, 255, 134, 0.8);
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 8px;
  margin-top: 8px;
  font-size: 0.85rem;
}

.reset-filter-btn:hover {
  color: rgba(4, 255, 134, 1);
}

/* Media type badges */
.media-type-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.video-badge {
  background: #E91E63;
  color: white;
}

.image-badge {
  background: #2196F3;
  color: white;
}

.doc-badge {
  background: #FF9800;
  color: white;
}

/* Animations */
@keyframes gradientSlide {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Mobile Responsive Design */
@media screen and (max-width: 768px) {
  .collapsed-thumbnail {
    width: 28px;
    height: 28px;
  }

  .more-indicator {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }

  .collapsed-text {
    font-size: 0.8rem;
  }

  .carousel-item {
    width: 160px;
    height: 140px;
  }

  .scroll-arrow {
    width: 32px;
    height: 32px;
  }

  .upload-title {
    font-size: 0.8rem;
  }

  .preview-content {
    width: 95%;
  }

  .upload-progress-container {
    padding: 15px;
    gap: 12px;
  }

  .upload-progress-spinner {
    width: 30px;
    height: 30px;
  }

  .upload-file-name {
    font-size: 0.8rem;
  }
}

/* Updated Upload Button Styling - Modern & Slick */
.upload-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f7fafc;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.theme--dark .upload-button {
  background-color: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.upload-button:hover:not(.disabled) {
  background-color: #edf2f7;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
}

.theme--dark .upload-button:hover:not(.disabled) {
  background-color: #4a5568;
}

.upload-button.disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Clean, modern upload text styling */
.upload-button span {
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  color: #4a5568;
  font-weight: 500;
}

.theme--dark .upload-button span {
  color: #e2e8f0;
}

/* Modern upload icon */
.upload-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon svg {
  stroke: #4a5568;
  stroke-width: 2px;
  width: 20px;
  height: 20px;
}

.theme--dark .upload-icon svg {
  stroke: #e2e8f0;
}

/* Simplified spinner */
.spinner {
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 2px solid #4a5568;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

.theme--dark .spinner {
  border-top-color: #e2e8f0;
  border-color: rgba(255, 255, 255, 0.1);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
  </style>
